import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import { Company, FilterVariant } from '../../types/company.types';

export type FetchCompanies = {
  enabled?: boolean;
  filter: FilterVariant;
  numberOfFetchedItems?: number;
};

type GetCompaniesResponseType = {
  data: Company[];
  totalPages: number;
};

export const useCompanies = ({
  enabled = true,
  filter,
  numberOfFetchedItems = 500,
}: FetchCompanies) => {
  const queryClient = useQueryClient();
  const { data, fetchNextPage, hasNextPage, isLoading } =
    useInfiniteQuery<GetCompaniesResponseType>({
      queryKey: [
        QueryKey.GET_COMPANIES,
        {
          filter,
          numberOfFetchedItems,
        },
      ],
      queryFn: ({ pageParam = 0 }) =>
        ApiService.get(
          { endpoint: BackendRoute.COMPANIES },
          {
            queryParams: {
              take: numberOfFetchedItems,
              skip: pageParam,
              filter: filter === 'all' ? '' : filter,
            },
          },
        ),
      enabled,
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.totalPages > allPages.length ? allPages.length * numberOfFetchedItems : undefined,
    });

  const companiesList = useMemo(
    () =>
      data?.pages?.reduce<Company[]>((acc, page) => {
        return [...acc, ...page.data];
      }, []),
    [data?.pages],
  );

  const invalidateQuery = () => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_COMPANIES] });
  };

  return {
    invalidateQuery,
    companies: companiesList || [],
    fetchNextPage,
    hasNextPage,
    isLoading,
  };
};
